import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { isBanner, LazyLoadOptions, LISTER_TILES_ID } from '@as-react/common-types';
import { Box, Grid, useBreakpoints, generateBreakpointsMap } from '@yonderland/andes-react';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { PLACEHOLDER_TILES } from '../../constants/GeneralConstants';
import compareActions from '../../redux/compare/actions/CompareActions.creators';
import { wishListActions } from '../../redux/wishList/actions/WishListActions.creators';
import { BannerTile } from '../lister/banner-tile/BannerTile';
import { GridTilePlaceholder } from '../lister/grid-tile-placeholder/GridTilePlaceholder';
import { RowBanner } from '../lister/row-banner/RowBanner';
import { useProductListerContext } from '../product-lister/context';
import { ProductListerTile } from './ProductListerTile';
const GRID_COLUMNS = generateBreakpointsMap({
    xs: 2,
    md: 3,
    xl: 4,
});
const shouldShowContentBlock = (itemsLength, activeBreakpoint, visibleInRow, shownContentBlocks) => {
    const itemsPerRow = GRID_COLUMNS[activeBreakpoint];
    const previousRowWithItems = visibleInRow - (1 + shownContentBlocks);
    const minimumItemsToFillPreviousRow = itemsPerRow * previousRowWithItems - itemsPerRow;
    return itemsLength > minimumItemsToFillPreviousRow;
};
export const ProductListerGrid = ({ isLoading, items, contentBlocks, singleActiveSizeFilterKeyName, }) => {
    const { canonicalUrl } = useAemConfig();
    const { urlState: { page }, } = useProductListerContext();
    const { active: activeBreakpoint } = useBreakpoints();
    const dispatch = useDispatch();
    const determineLazyLoadState = (index) => index < PLACEHOLDER_TILES.length
        ? LazyLoadOptions.NO_LAZY_LOAD
        : index < PLACEHOLDER_TILES.length * 1.5
            ? LazyLoadOptions.LAZY_PRE_LOAD
            : LazyLoadOptions.LAZY_LOAD;
    const shownContentBlocks = useMemo(() => contentBlocks
        .sort((a, b) => a.visibleInRow - b.visibleInRow)
        .reduce((acc, contentBlock) => {
        if ((!contentBlock.visibleOnPage || contentBlock.visibleOnPage === (page || 0) + 1) &&
            shouldShowContentBlock(items.length, activeBreakpoint, contentBlock.visibleInRow, acc.length)) {
            return [...acc, contentBlock];
        }
        return acc;
    }, []), [contentBlocks, page, items.length, activeBreakpoint]);
    const handleClickWishlist = (product, activeColour, activeSize) => dispatch(wishListActions.toggleProduct({
        productCode: product.productCode,
        productId: product.productId,
        colorId: activeColour.colourId,
        sku: activeSize === null || activeSize === void 0 ? void 0 : activeSize.sku,
    }));
    const handleClickCompare = (product, activeColour, productURL) => dispatch(compareActions.createUpdateOnListerAction({
        brand: product.brand,
        color: activeColour.colourId,
        image: activeColour.image,
        productCode: product.productCode,
        selected: false,
        id: product.productId,
        title: product.title,
        gender: product.genderCode,
        url: productURL,
        colorDescription: activeColour.description,
        collapsedComparePopup: true,
    }));
    const generateRowBanner = (contentBlock, index) => (_jsx(Grid.Cell, { paddingBlockEnd: "10", rowOffset: contentBlock.visibleInRow * 2 - 1, span: "full", children: _jsx(RowBanner, { content: contentBlock.content }) }, `row-banner-item-${index}`));
    return (_jsxs(Grid, { id: LISTER_TILES_ID, testId: LISTER_TILES_ID, columns: GRID_COLUMNS, align: "start", gapX: {
            xs: 2,
            sm: 3,
        }, gapY: "0", children: [isLoading
                ? PLACEHOLDER_TILES.map((_, index) => (_jsx(Box, { paddingBlockEnd: "10", children: _jsx(GridTilePlaceholder, {}, `grid-item-${index}`) })))
                : items.map((item, index) => {
                    var _a;
                    const shouldLazyLoadImage = determineLazyLoadState(index);
                    if (isBanner(item)) {
                        const { image, url, alt } = item.images[0];
                        return (_jsx(Grid, { columns: 1, rows: "subgrid", rowSpan: 2, gap: 0, children: _jsx(Grid.Cell, { rowSpan: "full", children: _jsx(Box, { paddingBlockEnd: "10", display: "inline", children: _jsx(BannerTile, { lazy: shouldLazyLoadImage === LazyLoadOptions.LAZY_LOAD, url: url, image: image, andesLayout: true, zitchaInfo: item.adId && item.isSponsored
                                            ? {
                                                adId: item.adId,
                                                isSponsored: item.isSponsored,
                                                position: (_a = item === null || item === void 0 ? void 0 : item.listerPosition) !== null && _a !== void 0 ? _a : 0,
                                                impressionURL: canonicalUrl !== null && canonicalUrl !== void 0 ? canonicalUrl : '',
                                            }
                                            : undefined, alt: alt }) }) }) }, `grid-item-${item.listerPosition}-${index}`));
                    }
                    return (_jsx(ProductListerTile, { singleActiveSizeFilterKeyName: singleActiveSizeFilterKeyName, position: index + 1, shouldLazyLoadImage: shouldLazyLoadImage === LazyLoadOptions.LAZY_LOAD, product: item, onClickWishlist: handleClickWishlist, onClickCompare: handleClickCompare }, `${item.productCode}${item.colourId}${index}`));
                }), shownContentBlocks.map(generateRowBanner)] }));
};
